import { Controller } from "@hotwired/stimulus"

// Live-previews the web widget's appearance. The iframe is loaded ONCE (initial paint with the current
// accent colours); subsequent edits are pushed to the already-running widget via postMessage, so it
// re-themes in place with no reload/flash:
//
//   accent colours     -> shipped:preview-theme  -> wrapper.applyTheme
//   per-element tokens -> shipped:preview-tokens -> wrapper.applyThemeTokens
//   showcase scene     -> shipped:preview-scene  -> wrapper.setPreviewScene
//   custom CSS         -> shipped:preview-css    -> wrapper.applyCustomCSS
//   header logo        -> reload (query string)  -> config.headerLogoStyle / headerLogoUrl
//
// The preview page falls back to a self-reload if its widget SDK can't theme live. Accent inputs declare
// their param key via data-theme-key; token inputs declare theirs via data-token.
export default class extends Controller {
  static targets = ["input", "tokenInput", "frame", "sceneButton", "customCssInput", "logoStyleInput"]
  static values = { url: String }

  connect() {
    this.scene = "welcome"
    this.load()
    this.markActiveScene()

    // This page refreshes by morphing (<meta name="turbo-refresh-method" content="morph">). A morph diffs
    // the live DOM against fresh server HTML — which renders the iframe with NO src (we set it here) and
    // no active-segment classes, so it strips both straight back off. The elements survive the morph, so
    // Stimulus never re-runs connect(): without this the preview goes blank on every save.
    this.restoreAfterMorph = this.restoreAfterMorph.bind(this)
    document.addEventListener("turbo:morph", this.restoreAfterMorph)
  }

  disconnect() {
    clearTimeout(this.cssDebounce)
    document.removeEventListener("turbo:morph", this.restoreAfterMorph)
  }

  restoreAfterMorph() {
    // Reload only if the morph actually took the src: reloading a live preview would flash the widget
    // and throw away the scene the merchant is looking at. `this.scene` rides on the controller
    // instance, which survives, so the reload replays it via pushState on the iframe's load event.
    if (this.hasFrameTarget && !this.frameTarget.getAttribute("src")) this.load()
    this.markActiveScene()
  }

  // Push the current colours to the running preview widget — live, no reload.
  refresh() {
    this.post({ type: "shipped:preview-theme", theme: this.theme() })
    this.post({ type: "shipped:preview-tokens", tokens: this.tokens() })
  }

  // Custom CSS fires on every keystroke, so debounce it — unlike the colour inputs, a half-typed
  // selector is a parse error and re-theming on each character is wasted work.
  refreshCss() {
    clearTimeout(this.cssDebounce)
    this.cssDebounce = setTimeout(() => this.postCss(), 200)
  }

  postCss() {
    this.post({ type: "shipped:preview-css", css: this.customCss() })
  }

  customCss() {
    return this.hasCustomCssInputTarget ? this.customCssInputTarget.value : ""
  }

  // Drop an example into the editor: append rather than replace so a merchant can stack a few, and
  // let the widget re-theme immediately so the snippet explains itself.
  insertRecipe(event) {
    event.preventDefault()
    if (!this.hasCustomCssInputTarget) return

    const input = this.customCssInputTarget
    const snippet = event.currentTarget.dataset.css
    if (!snippet) return

    const existing = input.value.trimEnd()
    const next = existing ? `${existing}\n\n${snippet}\n` : `${snippet}\n`

    if (input.aceEditor) {
      // The ace editor owns what's on screen — writing `.value` alone wouldn't reach it, so push
      // the update through the editor session, which syncs back to the textarea itself.
      input.aceEditor.session.setValue(next)
      input.aceEditor.focus()
    } else {
      input.value = next
      input.focus()
      input.setSelectionRange(input.value.length, input.value.length)
    }
    this.postCss()
  }

  // A fresh iframe document only knows the accent colours (query string) and the SAVED tokens (fetched
  // from the API), so replay the unsaved token edits and the selected scene into it once it has loaded.
  pushState() {
    this.post({ type: "shipped:preview-tokens", tokens: this.tokens() })
    this.post({ type: "shipped:preview-scene", scene: this.scene })
    this.postCss()
  }

  selectScene(event) {
    event.preventDefault()
    this.scene = event.currentTarget.dataset.scene
    this.markActiveScene()
    this.post({ type: "shipped:preview-scene", scene: this.scene })
  }

  // Initial paint: load the preview document with the current colours in the query string.
  load() {
    if (!this.hasFrameTarget) return

    const params = new URLSearchParams()
    Object.entries(this.theme()).forEach(([key, value]) => {
      if (value) params.set(key, value)
    })

    // The header logo is part of the widget's boot config, not a live token, so the checked choice rides
    // along in the query string and picking a card re-boots the preview (each card's change -> load).
    const logoStyle = this.logoStyleInputTargets.find((input) => input.checked)
    if (logoStyle) params.set("header_logo_style", logoStyle.value)

    const query = params.toString()
    this.frameTarget.src = query ? `${this.urlValue}?${query}` : this.urlValue
  }

  theme() {
    const theme = {}
    this.inputTargets.forEach((input) => {
      theme[input.dataset.themeKey] = input.value
    })
    return theme
  }

  // Only the tokens the merchant has actually set. A blank field means "use the default", which the
  // widget represents as the token being absent — so blanks are dropped rather than sent as "".
  tokens() {
    const tokens = {}
    this.tokenInputTargets.forEach((input) => {
      if (input.value) tokens[input.dataset.token] = input.value
    })
    return tokens
  }

  markActiveScene() {
    this.sceneButtonTargets.forEach((button) => {
      const active = button.dataset.scene === this.scene
      button.classList.toggle("bg-white", active)
      button.classList.toggle("shadow-sm", active)
    })
  }

  post(message) {
    if (!this.hasFrameTarget || !this.frameTarget.contentWindow) return

    // Same-origin target: the preview iframe src is our own preview action, so scope the message to
    // this origin rather than broadcasting the merchant's in-progress theme to any frame.
    this.frameTarget.contentWindow.postMessage(message, window.location.origin)
  }
}
